import type { Locale } from "@/types";

export type PlatformToolId =
  | "ai-planner"
  | "voip-sms-mms"
  | "ai-hotline"
  | "crm"
  | "social-central"
  | "client-cards"
  | "sales-central";

/**
 * Names of the three "capability" locale namespaces a tool uses. Each tool
 * has three rendered sections between the direct-answer block and the
 * transformations block — one "blue accent card" with a features array,
 * one icon-grid with items, and one feature-grid with features. Locale
 * keys live under `platformTools.{toolId}.{sectionName}`.
 */
export interface PlatformToolSections {
  blueCard: string;
  iconGrid: string;
  featureGrid: string;
}

export interface PlatformToolMeta {
  id: PlatformToolId;
  /**
   * camelCase locale key used in src/locales/{fr,en}.json under
   * `platformTools.{localeKey}`. Required because tool ids are kebab-case
   * and JSON keys are camelCase — `ai-planner` vs `aiPlanner` etc.
   */
  localeKey: string;
  anchorId: string;
  slugs: Record<Locale, string>;
  /**
   * Populated for tools that have a live render branch. Used by the shared
   * ToolPage composition to look up the bespoke capability section keys.
   * Undefined for tools that haven't shipped yet (template renders 404).
   */
  sections?: PlatformToolSections;
}

export const PLATFORM_TOOLS: PlatformToolMeta[] = [
  {
    id: "ai-planner",
    localeKey: "aiPlanner",
    anchorId: "ai-planner",
    slugs: { fr: "planificateur-ia", en: "ai-planner" },
    sections: {
      blueCard: "briefingCard",
      iconGrid: "views",
      featureGrid: "taskIntelligence",
    },
  },
  {
    id: "voip-sms-mms",
    localeKey: "voipSmsMms",
    anchorId: "voip-sms-mms",
    slugs: { fr: "communications-unifiees", en: "unified-communications" },
    sections: {
      blueCard: "phoneLine",
      iconGrid: "messaging",
      featureGrid: "unifiedThread",
    },
  },
  {
    id: "ai-hotline",
    localeKey: "aiHotline",
    anchorId: "ai-hotline",
    slugs: { fr: "hotline-ia", en: "ai-hotline" },
    sections: {
      blueCard: "liveAnswer",
      iconGrid: "coverage",
      featureGrid: "voice",
    },
  },
  {
    id: "crm",
    localeKey: "crm",
    anchorId: "crm",
    slugs: { fr: "crm", en: "crm" },
    sections: {
      blueCard: "clientCard",
      iconGrid: "pipeline",
      featureGrid: "enrichment",
    },
  },
  {
    id: "social-central",
    localeKey: "socialCentral",
    anchorId: "social-central",
    slugs: { fr: "social-central", en: "social-central" },
    sections: {
      blueCard: "console",
      iconGrid: "posting",
      featureGrid: "intelligence",
    },
  },
  {
    id: "client-cards",
    localeKey: "clientCards",
    anchorId: "client-cards",
    slugs: { fr: "fiches-clients", en: "client-cards" },
    sections: {
      blueCard: "insights",
      iconGrid: "dashboards",
      featureGrid: "coaching",
    },
  },
  {
    id: "sales-central",
    localeKey: "salesCentral",
    anchorId: "sales-central",
    slugs: { fr: "sales-central", en: "sales-central" },
    sections: {
      blueCard: "salesScripts",
      iconGrid: "prospecting",
      featureGrid: "dealCoaching",
    },
  },
];

export const PLATFORM_TOOL_IDS: PlatformToolId[] = PLATFORM_TOOLS.map((t) => t.id);

export function getPlatformTool(id: string): PlatformToolMeta | undefined {
  return PLATFORM_TOOLS.find((t) => t.id === id);
}

export function getPlatformToolByLocaleSlug(
  slug: string,
  locale: Locale,
): PlatformToolMeta | undefined {
  return PLATFORM_TOOLS.find((t) => t.slugs[locale] === slug);
}
